// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/NotificationBadge.css";
import { getUser } from "../auth/auth";
import { RealtimeEvents } from "../realtime/events";
import { useRealtimeSocket } from "../realtime/useRealtime";
import {
  notificationsApi,
  useGetNotificationsQuery,
  useMarkAllAsReadMutation,
  useMarkNotificationAsReadMutation,
} from "../store/api/notificationsApi";
import { store } from "../store/store";
import {
  getNotificationPayload,
  isBellEligibleNotification,
  isAchievementUnlockedNotification,
} from "../utils/notifications";

export default function NotificationBadge() {
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const realtime = useRealtimeSocket();
  const user = getUser();
  const userId = user?.id ? String(user.id) : "";

  const { data: notificationsData = [], isFetching } = useGetNotificationsQuery(userId, {
    skip: !userId,
  });
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();

  const notifications = useMemo(
    () => (notificationsData || []).filter((notification) => isBellEligibleNotification(notification)),
    [notificationsData],
  );
  const unreadCount = useMemo(
    () => (notifications || []).filter((notification) => !notification.read).length,
    [notifications],
  );

  useEffect(() => {
    if (!userId) return;

    const handleItem = (payload) => {
      const notification = payload?.notification;
      if (!notification) return;

      store.dispatch(
        notificationsApi.util.upsertQueryData("getNotifications", userId, [notification]),
      );
      store.dispatch(
        notificationsApi.util.updateQueryData("getNotifications", userId, (draft) => {
          const existing = (draft || []).filter((n) => String(n.id) !== String(notification.id));
            return [notification, ...existing];
          }),
      );
    };

    realtime.on(RealtimeEvents.NotificationsItemCreated, handleItem);

    return () => {
      realtime.off(RealtimeEvents.NotificationsItemCreated, handleItem);
    };
  }, [realtime, userId]);

  const handleBellClick = () => {
    setShowDropdown((prev) => !prev);
  };

  const getLegacyMatchKind = (notification) => {
    if (notification?.type !== "system") return null;

    const payload = getNotificationPayload(notification);
    const title = String(notification?.title || "").toLowerCase();
    const message = String(notification?.message || "").toLowerCase();
    const combined = `${title} ${message}`;

    if (!payload?.tradeId && !payload?.bountyId) return null;
    if (!combined.includes("match")) return null;

    if (
      combined.includes("looking for a card you have") ||
      combined.includes("matches a card you posted for trade")
    ) {
      return "trader";
    }

    if (
      combined.includes("you're looking for") ||
      combined.includes("matches your bounty") ||
      combined.includes("someone nearby is trading")
    ) {
      return "bounty_owner";
    }

    if (payload?.bountyId && !payload?.tradeId) return "bounty_owner";
    if (payload?.tradeId && !payload?.bountyId) return "trader";
    return "match";
  };

  const isEventReminderNotification = (notification) => {
    const payload = getNotificationPayload(notification);
    if (payload?.kind === "event_reminder" && payload?.conventionId) {
      return true;
    }
    return String(notification?.title || "").toLowerCase().includes("event reminder") && Boolean(payload?.conventionId);
  };

  const navigateToTradeHubMatch = (postType, postId) => {
    if (!postType || !postId) return false;
    navigate(`/trades?postType=${encodeURIComponent(postType)}&postId=${encodeURIComponent(postId)}`);
    return true;
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read && userId) {
      try {
        await markNotificationAsRead({ notificationId: notification.id, userId }).unwrap();
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    setShowDropdown(false);

    const payload = getNotificationPayload(notification);
    const legacyMatchKind = getLegacyMatchKind(notification);

    if (isAchievementUnlockedNotification(notification)) {
      navigate("/profile");
    } else if (notification.type === "bounty_match" && payload?.bountyId) {
      navigateToTradeHubMatch("bounty", payload.bountyId);
    } else if (notification.type === "trade_match" && payload?.tradeId) {
      navigateToTradeHubMatch("trade", payload.tradeId);
    } else if (isEventReminderNotification(notification)) {
      navigate(`/events/${payload.conventionId}`);
    } else if (legacyMatchKind && payload?.bountyId) {
      navigateToTradeHubMatch("bounty", payload.bountyId);
    } else if (legacyMatchKind && payload?.tradeId) {
      navigateToTradeHubMatch("trade", payload.tradeId);
    } else if (
      (notification.type === "trade_offer" || notification.type === "offer_sent") &&
      payload?.offerId
    ) {
      navigate(`/offer/${payload.offerId}`);
    } else if (notification.type === "trade_accepted" && payload?.offerId) {
      navigate(`/offer/${payload.offerId}`);
    } else if (notification.type === "trade_accepted") {
      if (payload?.conversationId) {
        navigate(`/messages/${payload.conversationId}`);
      } else {
        navigate("/messages");
      }
    } else if (
      notification.type === "trade_offer" ||
      notification.type === "offer_sent" ||
      notification.type === "trade_accepted"
    ) {
      navigate("/messages");
    } else if (notification.type.includes("dispute") || notification.type.includes("admin_resolved")) {
      navigate("/orders");
    } else if (notification.type.includes("strike") || notification.type.includes("suspended") || notification.type.includes("banned")) {
      navigate("/profile");
    }
  };

  const handleMarkAllRead = async () => {
    if (!userId) return;
    try {
      await markAllAsRead({ userId }).unwrap();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const getNotificationIcon = (type) => {
    if (type === "bounty_match") return "🔥";
    if (type === "trade_match") return "💡";
    if (type === "achievement_unlocked") return "🏆";
    if (type === "trade_offer") return "🤝";
    if (type === "offer_sent") return "📤";
    if (type === "trade_accepted") return "✅";
    if (type.includes("dispute_opened")) return "⚠️";
    if (type.includes("seller_responded")) return "💬";
    if (type.includes("admin_resolved")) return "✅";
    if (type.includes("strike_warning")) return "⚠️";
    if (type.includes("suspended")) return "🚫";
    if (type.includes("banned")) return "❌";
    return "🔔";
  };

  const getNotificationVisual = (notification) => {
    if (isEventReminderNotification(notification)) return "📅";
    if (isAchievementUnlockedNotification(notification)) return "🏆";
    const legacyMatchKind = getLegacyMatchKind(notification);
    if (legacyMatchKind === "trader") return "🔥";
    if (legacyMatchKind === "bounty_owner" || legacyMatchKind === "match") return "💡";
    return getNotificationIcon(String(notification?.type || ""));
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-container">
      <button className="notification-bell" onClick={handleBellClick}>
        <img src="/notification.png" alt="Notifications" className="notification-bell-icon" />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
      </button>

      {showDropdown && (
        <>
          <div className="notification-dropdown">
            <div className="notification-header">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <button className="mark-all-read" onClick={handleMarkAllRead}>
                  Mark all read
                </button>
              )}
            </div>

            {isFetching ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="empty-icon">🔔</div>
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.read ? "unread" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon">{getNotificationVisual(notification)}</div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{formatTime(notification.createdAt)}</div>
                    </div>
                    {!notification.read && <div className="unread-dot"></div>}
                  </div>
                ))}

                {notifications.length > 10 && (
                  <div className="notification-footer">
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate("/notifications");
                      }}
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="notification-backdrop" onClick={() => setShowDropdown(false)} />
        </>
      )}
    </div>
  );
}
